// client/src/components/admin/AdminMatchesView.js
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import PageLayout from '../common/layout/PageLayout'
import useAuth from '../../hooks/useAuth'
import ApiMatches from '../../api/ApiMatches'
import ApiLeagues from '../../api/ApiLeagues'
import toastr from 'toastr'
import './AdminMatchesView.css'

function formatDateTime(dateTimeStr) {
  if (!dateTimeStr) return '—'
  const date = new Date(dateTimeStr)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

function formatLocation(match) {
  if (!match.home_court_name) return '—'
  const parts = [match.home_court_name]
  if (match.home_court_city && match.home_court_state) {
    parts.push(`${match.home_court_city}, ${match.home_court_state}`)
  }
  return parts.join(' • ')
}

function getStatusBadgeClass(status) {
  switch (status) {
    case 'scheduled':
      return 'badge bg-success'
    case 'created':
      return 'badge bg-secondary'
    case 'normal':
    case 'home_retired':
    case 'away_retired':
    case 'i_retired':
    case 'opponent_retired':
      return 'badge bg-primary'
    default:
      return 'badge bg-secondary'
  }
}

const DEFAULT_STATUS_FILTER = 'all'

function AdminMatchesView() {
  const { isAdmin, loading: authLoading } = useAuth()
  const [matches, setMatches] = useState([])
  const [totalMatches, setTotalMatches] = useState(0)
  const [leagues, setLeagues] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [leagueError, setLeagueError] = useState(null)
  const [lastRefresh, setLastRefresh] = useState(null)

  // Filters
  const [dateRangeFilter, setDateRangeFilter] = useState('all') // 'next7', 'next30', 'all'
  const [showPastMatches, setShowPastMatches] = useState(false)
  const [leagueFilter, setLeagueFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER)
  const [searchQuery, setSearchQuery] = useState('')

  // Sorting
  const [sortColumn, setSortColumn] = useState('match_datetime')
  const [sortDirection, setSortDirection] = useState('asc')

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 50

  // Fetch leagues for filter dropdown
  useEffect(() => {
    async function fetchLeagues() {
      try {
        const data = await ApiLeagues.getAll()
        setLeagues(data || [])
        setLeagueError(null)
      } catch (err) {
        console.error('Failed to fetch leagues:', err)
        setLeagueError('Failed to load leagues for filter')
      }
    }
    if (isAdmin) {
      fetchLeagues()
    }
  }, [isAdmin])

  // Fetch matches with server-side pagination and sorting
  const fetchMatches = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const options = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        sortBy: sortColumn,
        sortDirection: sortDirection,
      }

      // Add league filter if selected
      if (leagueFilter !== 'all') {
        options.leagueId = parseInt(leagueFilter, 10)
      }

      // Add status filter if selected
      if (statusFilter !== 'all') {
        options.statusFilter = statusFilter
      }

      if (showPastMatches) {
        options.includePast = true
      }

      // Add date range filter
      if (dateRangeFilter === 'next7') {
        const now = new Date()
        const next7 = new Date()
        next7.setDate(now.getDate() + 7)
        options.startDate = now.toISOString()
        options.endDate = next7.toISOString()
      } else if (dateRangeFilter === 'next30') {
        const now = new Date()
        const next30 = new Date()
        next30.setDate(now.getDate() + 30)
        options.startDate = now.toISOString()
        options.endDate = next30.toISOString()
      }

      const response = await ApiMatches.getAllUpcomingMatches(options)
      setMatches(response.matches || [])
      setTotalMatches(response.pagination?.total || 0)
      setLastRefresh(new Date())
    } catch (err) {
      console.error('Failed to fetch matches:', err)
      setError('Failed to load matches. Please try again.')
      toastr.error('Failed to load matches')
    } finally {
      setLoading(false)
    }
  }, [
    currentPage,
    dateRangeFilter,
    leagueFilter,
    statusFilter,
    showPastMatches,
    sortColumn,
    sortDirection,
  ])

  useEffect(() => {
    if (isAdmin && !authLoading) {
      fetchMatches()
    }
  }, [isAdmin, authLoading, fetchMatches])

  // Client-side filtering by search query (server doesn't support search yet)
  const filteredMatches = useMemo(() => {
    if (!searchQuery.trim()) return matches

    const query = searchQuery.toLowerCase()
    return matches.filter((match) => {
      const homePlayer =
        `${match.home_first_name || ''} ${match.home_last_name || ''}`.toLowerCase()
      const awayPlayer =
        `${match.away_first_name || ''} ${match.away_last_name || ''}`.toLowerCase()
      const league = (match.league_name || '').toLowerCase()
      const division = (match.division_name || '').toLowerCase()

      return (
        homePlayer.includes(query) ||
        awayPlayer.includes(query) ||
        league.includes(query) ||
        division.includes(query)
      )
    })
  }, [matches, searchQuery])

  const totalPages = Math.ceil(totalMatches / itemsPerPage)

  // Handle column sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
    setCurrentPage(1) // Reset to first page when sorting changes
  }

  // Get ARIA sort attribute for column
  const getAriaSortValue = (column) => {
    if (sortColumn !== column) return 'none'
    return sortDirection === 'asc' ? 'ascending' : 'descending'
  }

  const getSortIcon = (column) => {
    if (sortColumn !== column) return null
    return sortDirection === 'asc' ? '▲' : '▼'
  }

  // Helper for sortable header props
  const getSortableHeaderProps = (column) => ({
    className: 'admin-matches-sortable-header',
    onClick: () => handleSort(column),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleSort(column)
      }
    },
    role: 'button',
    tabIndex: 0,
    'aria-sort': getAriaSortValue(column),
  })

  if (authLoading) {
    return (
      <PageLayout title="Admin Matches">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!isAdmin) {
    return (
      <PageLayout title="Admin Matches">
        <div className="alert alert-danger">
          You do not have permission to view this page.
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="Admin Matches">
      <div className="admin-matches-container">
        {/* Header with filters */}
        <div className="admin-matches-header">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
            <div>
              <h4 className="mb-1">Admin Matches</h4>
              <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                {filteredMatches.length} match
                {filteredMatches.length !== 1 ? 'es' : ''} found
                {lastRefresh && (
                  <span className="ms-2">
                    • Last updated {lastRefresh.toLocaleTimeString()}
                  </span>
                )}
              </p>
            </div>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={fetchMatches}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Refresh
            </button>
          </div>

          {/* Filters bar */}
          <div className="admin-matches-filters">
            <div className="row g-3 mb-4">
              {/* Date range filter */}
              <div className="col-12 col-md-6 col-lg-3">
                <label
                  htmlFor="dateRangeFilter"
                  className="form-label"
                  style={{ fontSize: '0.875rem', fontWeight: 500 }}
                >
                  Date Range
                </label>
                <select
                  id="dateRangeFilter"
                  className="form-select form-select-sm"
                  value={dateRangeFilter}
                  onChange={(e) => {
                    setDateRangeFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                >
                  <option value="all">All Upcoming</option>
                  <option value="next7">Next 7 Days</option>
                  <option value="next30">Next 30 Days</option>
                </select>
              </div>

              {/* League filter */}
              <div className="col-12 col-md-6 col-lg-3">
                <label
                  htmlFor="leagueFilter"
                  className="form-label"
                  style={{ fontSize: '0.875rem', fontWeight: 500 }}
                >
                  League
                </label>
                <select
                  id="leagueFilter"
                  className="form-select form-select-sm"
                  value={leagueFilter}
                  onChange={(e) => {
                    setLeagueFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                >
                  <option value="all">All Leagues</option>
                  {leagues.map((league) => (
                    <option key={league.id} value={league.id}>
                      {league.name}
                    </option>
                  ))}
                </select>
                {leagueError && (
                  <small className="text-warning d-block mt-1">
                    ⚠️ {leagueError}
                  </small>
                )}
              </div>

              {/* Status filter */}
              <div className="col-12 col-md-6 col-lg-3">
                <label
                  htmlFor="statusFilter"
                  className="form-label"
                  style={{ fontSize: '0.875rem', fontWeight: 500 }}
                >
                  Status
                </label>
                <select
                  id="statusFilter"
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                >
                  <option value="all">Scheduled + Played</option>
                  <option value="scheduled">Scheduled Only</option>
                  <option value="normal">Completed Only</option>
                  <option value="created">Created Only</option>
                </select>
              </div>

              {/* Search box */}
              <div className="col-12 col-md-6 col-lg-3">
                <label
                  htmlFor="searchQuery"
                  className="form-label"
                  style={{ fontSize: '0.875rem', fontWeight: 500 }}
                >
                  Search
                </label>
                <input
                  id="searchQuery"
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Player, league, division..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setCurrentPage(1)
                  }}
                />
              </div>
            </div>
            <div className="col-12">
              <div className="form-check">
                <input
                  id="showPastMatches"
                  type="checkbox"
                  className="form-check-input"
                  checked={showPastMatches}
                  onChange={(e) => {
                    setShowPastMatches(e.target.checked)
                    setCurrentPage(1)
                  }}
                  data-testid="show-past-matches-checkbox"
                />
                <label
                  htmlFor="showPastMatches"
                  className="form-check-label"
                  style={{ fontSize: '0.875rem' }}
                >
                  Show past matches
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading matches...</span>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && filteredMatches.length === 0 && (
          <div className="admin-matches-empty-state">
            <i className="bi bi-calendar-x"></i>
            <h5>No Matches Found</h5>
            <p className="text-muted">
              {searchQuery ||
              leagueFilter !== 'all' ||
              statusFilter !== DEFAULT_STATUS_FILTER
                ? 'No matches found matching your filters. Try adjusting your search criteria.'
                : 'There are no scheduled or played matches across any league.'}
            </p>
          </div>
        )}

        {/* Desktop Table View */}
        {!loading && !error && filteredMatches.length > 0 && (
          <>
            <div className="admin-matches-table-container d-none d-lg-block">
              <table className="admin-matches-table">
                <thead>
                  <tr>
                    <th
                      {...getSortableHeaderProps('match_datetime')}
                      style={{ width: '140px' }}
                    >
                      Date/Time
                      <span className="admin-matches-sort-indicator">
                        {getSortIcon('match_datetime')}
                      </span>
                    </th>
                    <th
                      {...getSortableHeaderProps('league_name')}
                      style={{ width: '150px' }}
                    >
                      League
                      <span className="admin-matches-sort-indicator">
                        {getSortIcon('league_name')}
                      </span>
                    </th>
                    <th
                      {...getSortableHeaderProps('division_name')}
                      style={{ width: '120px' }}
                    >
                      Division
                      <span className="admin-matches-sort-indicator">
                        {getSortIcon('division_name')}
                      </span>
                    </th>
                    <th
                      {...getSortableHeaderProps('home_player')}
                      style={{ width: '150px' }}
                    >
                      Home Player
                      <span className="admin-matches-sort-indicator">
                        {getSortIcon('home_player')}
                      </span>
                    </th>
                    <th
                      {...getSortableHeaderProps('away_player')}
                      style={{ width: '150px' }}
                    >
                      Away Player
                      <span className="admin-matches-sort-indicator">
                        {getSortIcon('away_player')}
                      </span>
                    </th>
                    <th style={{ width: '180px' }}>Location</th>
                    <th
                      {...getSortableHeaderProps('status')}
                      style={{ width: '100px' }}
                    >
                      Status
                      <span className="admin-matches-sort-indicator">
                        {getSortIcon('status')}
                      </span>
                    </th>
                    <th style={{ width: '80px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMatches.map((match) => (
                    <tr key={match.id}>
                      <td className="admin-matches-cell-datetime">
                        {formatDateTime(match.match_datetime)}
                      </td>
                      <td
                        className="admin-matches-cell-league"
                        title={match.league_name}
                      >
                        {match.league_name || '—'}
                      </td>
                      <td className="admin-matches-cell-division">
                        {match.division_name || '—'}
                      </td>
                      <td className="admin-matches-cell-player">
                        <div className="admin-matches-player-name">
                          {match.home_first_name} {match.home_last_name}
                        </div>
                        {match.home_email && (
                          <div className="admin-matches-player-email">
                            {match.home_email}
                          </div>
                        )}
                      </td>
                      <td className="admin-matches-cell-player">
                        <div className="admin-matches-player-name">
                          {match.away_first_name} {match.away_last_name}
                        </div>
                        {match.away_email && (
                          <div className="admin-matches-player-email">
                            {match.away_email}
                          </div>
                        )}
                      </td>
                      <td
                        className="admin-matches-cell-location"
                        title={formatLocation(match)}
                      >
                        {formatLocation(match)}
                      </td>
                      <td className="admin-matches-cell-status">
                        <span className={getStatusBadgeClass(match.status)}>
                          {match.status}
                        </span>
                      </td>
                      <td className="admin-matches-cell-actions">
                        <div className="d-flex gap-1">
                          <a
                            href={`/match-score-entry?matchId=${match.id}`}
                            className="btn btn-sm btn-outline-secondary"
                            title="View match details"
                            aria-label="View match details"
                          >
                            <i className="bi bi-eye" aria-hidden="true"></i>
                          </a>
                          <a
                            href={`/leagues/${match.league_id}`}
                            className="btn btn-sm btn-outline-secondary"
                            title="View league"
                            aria-label="View league landing page"
                          >
                            <i className="bi bi-trophy" aria-hidden="true"></i>
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="admin-matches-mobile-view d-lg-none">
              {filteredMatches.map((match) => (
                <div
                  key={match.id}
                  className="admin-matches-mobile-card card mb-3"
                >
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <h6 className="mb-1">
                          {match.home_first_name} {match.home_last_name} vs{' '}
                          {match.away_first_name} {match.away_last_name}
                        </h6>
                        <small className="text-muted">
                          {formatDateTime(match.match_datetime)}
                        </small>
                      </div>
                      <span className={getStatusBadgeClass(match.status)}>
                        {match.status}
                      </span>
                    </div>
                    <div className="mb-2" style={{ fontSize: '0.875rem' }}>
                      <div>
                        <strong>League:</strong> {match.league_name || '—'}
                      </div>
                      <div>
                        <strong>Division:</strong> {match.division_name || '—'}
                      </div>
                      <div>
                        <strong>Location:</strong> {formatLocation(match)}
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <a
                        href={`/match-score-entry?matchId=${match.id}`}
                        className="btn btn-sm btn-outline-secondary"
                      >
                        <i className="bi bi-eye me-1"></i>
                        View Match
                      </a>
                      <a
                        href={`/leagues/${match.league_id}`}
                        className="btn btn-sm btn-outline-secondary"
                      >
                        <i className="bi bi-trophy me-1"></i>
                        League
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="admin-matches-pagination">
                <nav aria-label="Matches pagination">
                  <ul className="pagination pagination-sm justify-content-center mb-0">
                    <li
                      className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                      >
                        Previous
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, i) => {
                      const pageNum = i + 1
                      // Show first, last, current, and pages around current
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 &&
                          pageNum <= currentPage + 1)
                      ) {
                        return (
                          <li
                            key={pageNum}
                            className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          </li>
                        )
                      } else if (
                        pageNum === currentPage - 2 ||
                        pageNum === currentPage + 2
                      ) {
                        return (
                          <li key={pageNum} className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        )
                      }
                      return null
                    })}
                    <li
                      className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="Next page"
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
                <p
                  className="text-center text-muted mt-2 mb-0"
                  style={{ fontSize: '0.875rem' }}
                >
                  Page {currentPage} of {totalPages} ({totalMatches} total
                  matches)
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </PageLayout>
  )
}

export default AdminMatchesView
